'use client';

import { EyeIcon, EyeOffIcon } from 'lucide-react';
import type { InputHTMLAttributes, ReactNode, WheelEvent } from 'react';
import { forwardRef, useState } from 'react';

import { StatusIcon } from '@/components/icons/status-icon';
import { Label } from '@/components/ui/label';
import { cn } from '@/utils';

interface InputBaseProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  disableAutoFill?: boolean;
}

export interface InputProps extends InputBaseProps {
  label?: ReactNode;
  labelClassname?: string;
  wrapperClassName?: string;
  orientation?: 'horizontal' | 'vertical';
  isPassword?: boolean;
}

const InputBase = forwardRef<HTMLInputElement, InputBaseProps>(
  ({ className, id, error, disableAutoFill = false, autoComplete, onWheel, type, ...props }, ref) => {
    const isAutofillDisabled = disableAutoFill ? 'off' : (autoComplete ?? 'on');

    const handleWheel = (event: WheelEvent<HTMLInputElement>) => {
      if (type === 'number') {
        event.currentTarget.blur();
      }
      onWheel?.(event);
    };

    return (
      <input
        id={id}
        type={type}
        autoComplete={isAutofillDisabled}
        className={cn(
          `flex h-9 px-3 py-1 w-full rounded-md
              border border-input
              ring-offset-background
              text-base md:text-sm font-medium
              file:border-0 file:bg-transparent file:text-sm
              placeholder:text-gray-300
              disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-black`,
          error === true
          && 'border-destructive border bg-red-100 text-destructive placeholder:text-red-300',
          className,
        )}
        ref={ref}
        onWheel={handleWheel}
        {...props}
      />
    );
  },
);

const InputPassword = forwardRef<HTMLInputElement, InputBaseProps>((props, ref) => {
  const [passwordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible(prevState => !prevState);
  };

  return (
    <>
      <InputBase ref={ref} {...props} type={passwordVisible ? 'text' : 'password'} />
      <button
        type="button"
        onClick={togglePasswordVisibility}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
      >
        {passwordVisible ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
      </button>
    </>
  );
},
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ id, label, labelClassname, error, wrapperClassName, orientation = 'vertical', isPassword = false, ...props }, ref) => {
    const CurrentInput = isPassword ? InputPassword : InputBase;

    return (
      <div
        className={cn(
          'space-y-2',
          wrapperClassName !== undefined && `${wrapperClassName}`,
          orientation === 'horizontal' && 'sm:grid sm:grid-cols-2  sm:w-full sm:items-center',
        )}
      >
        {label !== undefined && (
          <Label
            {...(id !== undefined ? { htmlFor: id } : {})}
            className={cn(
              error === true && 'text-destructive',
              labelClassname,
              orientation === 'horizontal' && 'sm:w-full',
            )}
          >
            {label}
          </Label>
        )}

        <div className={cn('relative', orientation === 'horizontal' && 'sm:w-full')}>
          <CurrentInput ref={ref} id={id} error={error} {...props} />

          {!isPassword && error === true && <StatusIcon success={false} className="absolute right-2 top-1/2 -translate-y-1/2" />}
        </div>
      </div>
    );
  },
);

Input.displayName = 'Input';
export { Input };
